import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useUserContext } from "../../utils/UserContext";
import { tincPermis } from "../../utils/Permisos";
import { mapToPresentable, mapejos } from "../../utils/Mapejos";
import { API_URL } from "../../utils/config";
import { formatAddress } from "../../utils/Utilitats";
import TeclaEscEnter from "../../utils/Teclaescenter";
import {
  aplicarValidacio,
  validarCodiPostal,
  validarEmail,
  validarTelefon,
} from "../../utils/Validacions";
import AjudaEntitats from "./AjudaEntitats";
import { BotoAjuda } from "./AjudaModal";

function idSucursal(s) {
  return s ? Number(s.idSucursals) : null;
}

function nomProveidor(proveidor) {
  if (!proveidor) return "";
  const tipus = mapToPresentable(proveidor.TipusEntitat, mapejos.tipusEntitat);
  const cognoms = [proveidor.Cognom1, proveidor.Cognom2].filter(Boolean).join(" ");
  if (["Empresa", "Autònom", "Autonom"].includes(tipus) || ["Empresa", "Autonom"].includes(proveidor.TipusEntitat)) {
    return proveidor.NomComercial?.trim() ? proveidor.NomComercial : proveidor.Nom;
  }
  return [cognoms, proveidor.Nom].filter((x) => x && String(x).trim()).join(", ");
}

function Sucursalsproveidor({ proveidor, onCancel, subnav }) {
  const [activeTab, setActiveTab] = useState("table");
  const [sucursals, setSucursals] = useState([]);
  const [selected, setSelected] = useState(null);
  const [menuObert, setMenuObert] = useState(null);
  const [menuPos, setMenuPos] = useState(null);
  const [menuAccions, setMenuAccions] = useState(false);
  const [mostraAjuda, setMostraAjuda] = useState(false);
  const [isNarrow, setIsNarrow] = useState(false);
  const menuRef = useRef(null);

  const { rolUsuari } = useUserContext();
  const pucCrear = tincPermis(rolUsuari, "CREAR");
  const pucConsultar = tincPermis(rolUsuari, "CONSULTAR");
  const pucModificar = tincPermis(rolUsuari, "MODIFICAR");
  const pucEsborrar = tincPermis(rolUsuari, "ESBORRAR");

  const handleEsc = useCallback(() => {
    if (activeTab === "table") onCancel?.();
    else setActiveTab("table");
  }, [activeTab, onCancel]);
  TeclaEscEnter(handleEsc, null);

  const fetchSucursals = async () => {
    if (!proveidor?.idProveidors) {
      setSucursals([]);
      return;
    }
    try {
      const response = await fetch(`${API_URL}/Sucursals.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idProveidors: proveidor.idProveidors }),
      });
      const data = await response.json();
      setSucursals(data.success ? data.sucursals || [] : []);
    } catch (error) {
      console.error("Error carregant sucursals:", error);
      setSucursals([]);
    }
  };

  useEffect(() => {
    setActiveTab("table");
    setSelected(null);
    fetchSucursals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proveidor?.idProveidors]);

  const reFetchToTable = () => {
    fetchSucursals();
    setActiveTab("table");
    setSelected(null);
  };

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const apply = () => setIsNarrow(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  useEffect(() => {
    const tancar = (e) => {
      if (e.target.closest(".dv2-menu") || e.target.closest(".dv2-menuBtn")) return;
      setMenuObert(null);
    };
    document.addEventListener("mousedown", tancar);
    return () => document.removeEventListener("mousedown", tancar);
  }, []);

  useEffect(() => {
    if (activeTab !== "consultar") setMenuAccions(false);
  }, [activeTab]);

  const obrirFitxa = (sucursal, mode) => {
    setMenuObert(null);
    setSelected(sucursal);
    setActiveTab(mode);
  };

  const obrirMenu = (e, sucursal) => {
    e.stopPropagation();
    const id = idSucursal(sucursal);
    if (menuObert === id) {
      setMenuObert(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const ample = 200;
    const alt = 160;
    const marge = 8;
    let top = rect.bottom + 4;
    if (top + alt > window.innerHeight - marge) {
      top = Math.max(marge, rect.top - alt - 4);
    }
    let left = rect.right - ample;
    if (left < marge) left = marge;
    if (left + ample > window.innerWidth - marge) {
      left = window.innerWidth - ample - marge;
    }
    setMenuPos({ top, left });
    setMenuObert(id);
  };

  const renderAccions = (sucursal) => (
    <div className="dv2-table-actions">
      <button
        type="button"
        className={`dv2-menuBtn ${menuObert === idSucursal(sucursal) ? "is-open" : ""}`}
        aria-label="Accions"
        onClick={(e) => obrirMenu(e, sucursal)}
      >
        …
      </button>
    </div>
  );

  const files = sucursals.map((s) => ({
    s,
    isSelected: idSucursal(selected) === idSucursal(s),
  }));
  const sucursalMenu = menuObert ? files.find(({ s }) => idSucursal(s) === menuObert)?.s : null;

  const renderTaula = () => (
    <section className="dv2-card">
      {files.length === 0 ? (
        <p className="dv2-empty">No hi ha sucursals d'aquest proveïdor.</p>
      ) : isNarrow ? (
        <div className="dv2-recordList" style={{ display: "flex" }}>
          {files.map(({ s, isSelected }) => (
            <article
              key={s.idSucursals}
              className={`dv2-record ${isSelected ? "is-selected" : ""}`}
              onClick={() => setSelected(isSelected ? null : s)}
              onDoubleClick={() => pucConsultar && obrirFitxa(s, "consultar")}
            >
              <div>
                <p className="dv2-record-title">{s.Alies || "—"}</p>
                <p className="dv2-record-meta">
                  {[s.Poblacio, s.TelefonFixe || s.TelefonMobil, s.Email1].filter(Boolean).join(" · ") || "—"}
                </p>
              </div>
              {renderAccions(s)}
            </article>
          ))}
        </div>
      ) : (
        <div className="dv2-tableWrap">
          <table className="dv2-table">
            <thead>
              <tr>
                <th>Àlies</th>
                <th>Població</th>
                <th>Telèfon</th>
                <th>Email</th>
                <th className="dv2-table-actions">Accions</th>
              </tr>
            </thead>
            <tbody>
              {files.map(({ s, isSelected }) => (
                <tr
                  key={s.idSucursals}
                  className={isSelected ? "is-selected" : ""}
                  onClick={() => setSelected(isSelected ? null : s)}
                  onDoubleClick={() => pucConsultar && obrirFitxa(s, "consultar")}
                >
                  <td>{s.Alies || "—"}</td>
                  <td>{s.Poblacio || "—"}</td>
                  <td>{s.TelefonFixe || s.TelefonMobil || "—"}</td>
                  <td>{s.Email1 || "—"}</td>
                  <td className="dv2-table-actions">{renderAccions(s)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {sucursalMenu &&
        menuPos &&
        createPortal(
          <div className="dv2-menu dv2-menu--fixed" ref={menuRef} style={{ top: menuPos.top, left: menuPos.left }}>
            {pucConsultar && (
              <button type="button" className="dv2-menu-item" onClick={() => obrirFitxa(sucursalMenu, "consultar")}>
                Consultar
              </button>
            )}
            {pucModificar && (
              <button type="button" className="dv2-menu-item" onClick={() => obrirFitxa(sucursalMenu, "modificar")}>
                Modificar
              </button>
            )}
            {pucEsborrar && (
              <button type="button" className="dv2-menu-item is-danger" onClick={() => obrirFitxa(sucursalMenu, "esborrar")}>
                Esborrar
              </button>
            )}
          </div>,
          document.body
        )}
    </section>
  );

  const canNavigate = useMemo(() => sucursals.length > 1 && !!selected, [sucursals.length, selected]);
  const idxConsultar = selected ? sucursals.findIndex((s) => idSucursal(s) === idSucursal(selected)) : -1;

  const goPrev = () => {
    if (!selected || sucursals.length === 0) return;
    const idx = sucursals.findIndex((s) => idSucursal(s) === idSucursal(selected));
    if (idx < 0) return;
    setSelected(sucursals[idx === 0 ? sucursals.length - 1 : idx - 1]);
  };
  const goNext = () => {
    if (!selected || sucursals.length === 0) return;
    const idx = sucursals.findIndex((s) => idSucursal(s) === idSucursal(selected));
    if (idx < 0) return;
    setSelected(sucursals[idx === sucursals.length - 1 ? 0 : idx + 1]);
  };

  const renderFitxa = (mode) => (
    <DetallsSucursal
      mode={mode}
      proveidor={proveidor}
      sucursal={mode === "nou" ? null : selected}
      onSave={(updated) => {
        if (mode === "nou") {
          reFetchToTable();
          return;
        }
        fetchSucursals();
        if (updated) setSelected((prev) => ({ ...(prev || {}), ...updated }));
        setActiveTab("consultar");
      }}
      onDeleted={reFetchToTable}
      onCancel={() => setActiveTab("table")}
      onPrev={canNavigate ? goPrev : undefined}
      onNext={canNavigate ? goNext : undefined}
      navIndex={idxConsultar >= 0 ? idxConsultar + 1 : 0}
      navTotal={sucursals.length}
    />
  );

  const titol =
    activeTab === "nou"
      ? "Nova sucursal"
      : activeTab === "esborrar"
      ? "Esborrar sucursal"
      : activeTab === "modificar"
      ? "Modificar sucursal"
      : activeTab === "consultar"
      ? "Sucursals · Fitxa"
      : "Sucursals";

  return (
    <div className="dv2-entity">
      {mostraAjuda && <AjudaEntitats pestanya="sucursals" onTancar={() => setMostraAjuda(false)} />}
      <div className="dv2-pageHeader">
        <div className="dv2-pageHeader-main">
          <div className={`dv2-pageHeader-icon ${activeTab === "esborrar" ? "is-danger" : ""}`} aria-hidden="true">
            {activeTab === "esborrar" ? "🗑️" : "🏢"}
          </div>
          <div>
            <h1 className="dv2-pageHeader-title">{titol}</h1>
            <p className="dv2-pageHeader-subtitle">{nomProveidor(proveidor) || "Delegacions del proveïdor"}</p>
          </div>
        </div>
        <div className="dv2-pageHeader-actions">
          <BotoAjuda title="Com funcionen les Sucursals" onClick={() => setMostraAjuda(true)} />
          <button type="button" className="dv2-btn" onClick={activeTab === "table" ? onCancel : () => setActiveTab("table")}>
            Tornar
          </button>
          {activeTab === "table" && pucCrear && (
            <button
              type="button"
              className="dv2-btn dv2-btn--primary"
              onClick={() => {
                setSelected(null);
                setActiveTab("nou");
              }}
            >
              Nova sucursal
            </button>
          )}
          {activeTab === "consultar" && pucModificar && (
            <button type="button" className="dv2-btn dv2-btn--primary" onClick={() => setActiveTab("modificar")}>
              Modificar
            </button>
          )}
          {activeTab === "consultar" && pucEsborrar && (
            <div style={{ position: "relative" }}>
              <button type="button" className="dv2-btn" onClick={() => setMenuAccions((v) => !v)}>
                Més accions
              </button>
              {menuAccions && (
                <div className="dv2-menu" style={{ right: 0, top: "42px" }}>
                  <button
                    type="button"
                    className="dv2-menu-item is-danger"
                    onClick={() => {
                      setMenuAccions(false);
                      setActiveTab("esborrar");
                    }}
                  >
                    Esborrar
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {subnav}
      {activeTab === "table" ? <div className="dv2-entity-body">{renderTaula()}</div> : renderFitxa(activeTab)}
    </div>
  );
}

function DetallsSucursal({
  mode,
  sucursal,
  proveidor,
  onSave,
  onDeleted,
  onCancel,
  onPrev,
  onNext,
  navIndex,
  navTotal,
}) {
  const isReadOnly = mode === "consultar" || mode === "esborrar";
  const [alies, setAlies] = useState("");
  const [telefonFixe, setTelefonFixe] = useState("");
  const [telefonMobil, setTelefonMobil] = useState("");
  const [telefonMobil2, setTelefonMobil2] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [email1, setEmail1] = useState("");
  const [email2, setEmail2] = useState("");
  const [horari, setHorari] = useState("");
  const [tipusVia, setTipusVia] = useState("");
  const [nomVia, setNomVia] = useState("");
  const [numVia, setNumVia] = useState("");
  const [escalaVia, setEscalaVia] = useState("");
  const [pis, setPis] = useState("");
  const [porta, setPorta] = useState("");
  const [poblacio, setPoblacio] = useState("");
  const [provincia, setProvincia] = useState("");
  const [pais, setPais] = useState("");
  const [codiPostal, setCodiPostal] = useState("");
  const [referencies, setReferencies] = useState("");

  const resetForm = useCallback(() => {
    setAlies("");
    setTelefonFixe("");
    setTelefonMobil("");
    setTelefonMobil2("");
    setWhatsapp("");
    setEmail1("");
    setEmail2("");
    setHorari("");
    setTipusVia("");
    setNomVia("");
    setNumVia("");
    setEscalaVia("");
    setPis("");
    setPorta("");
    setPoblacio("");
    setProvincia("");
    setPais("");
    setCodiPostal("");
    setReferencies("");
  }, []);

  useEffect(() => {
    if (sucursal && mode !== "nou") {
      setAlies(sucursal.Alies || "");
      setTelefonFixe(sucursal.TelefonFixe || "");
      setTelefonMobil(sucursal.TelefonMobil || "");
      setTelefonMobil2(sucursal.TelefonMobil2 || "");
      setWhatsapp(sucursal.Whatsapp || "");
      setEmail1(sucursal.Email1 || "");
      setEmail2(sucursal.Email2 || "");
      setHorari(sucursal.Horari || "");
      setTipusVia(sucursal.TipusVia || "");
      setNomVia(sucursal.NomVia || "");
      setNumVia(sucursal.NumVia || "");
      setEscalaVia(sucursal.EscalaVia || "");
      setPis(sucursal.Pis || "");
      setPorta(sucursal.Porta || "");
      setPoblacio(sucursal.Poblacio || "");
      setProvincia(sucursal.Provincia || "");
      setPais(sucursal.Pais || "");
      setCodiPostal(sucursal.CodiPostal || "");
      setReferencies(sucursal.Referencies || "");
    } else {
      resetForm();
    }
  }, [sucursal, mode, resetForm]);

  const adreca = {
    TipusVia: tipusVia,
    NomVia: nomVia,
    NumVia: numVia,
    EscalaVia: escalaVia,
    Pis: pis,
    Porta: porta,
    Poblacio: poblacio,
    Provincia: provincia,
    Pais: pais,
    CodiPostal: codiPostal,
    Referencies: referencies,
  };

  const handleSubmit = async () => {
    if (mode !== "esborrar") {
      const errors = [];
      const avisos = [];
      if (!alies.trim()) errors.push("L'àlies és obligatori.");
      if (!tipusVia.trim()) errors.push("El Tipus de via és obligatori.");
      if (!nomVia.trim()) errors.push("El Nom de la via és obligatori.");
      if (!numVia.trim()) errors.push("El Número de la via és obligatori.");
      if (!poblacio.trim()) errors.push("La Població és obligatòria.");
      if (!provincia.trim()) errors.push("La Província és obligatòria.");
      if (!pais.trim()) errors.push("El País és obligatori.");
      if (!codiPostal.trim()) errors.push("El Codi postal és obligatori.");
      aplicarValidacio(errors, avisos, "Telèfon fixe", validarTelefon(telefonFixe));
      aplicarValidacio(errors, avisos, "Mòbil 1", validarTelefon(telefonMobil));
      aplicarValidacio(errors, avisos, "Whatsapp", validarTelefon(whatsapp));
      aplicarValidacio(errors, avisos, "Mòbil 2", validarTelefon(telefonMobil2));
      aplicarValidacio(errors, avisos, "E-mail 1", validarEmail(email1));
      aplicarValidacio(errors, avisos, "E-mail 2", validarEmail(email2));
      aplicarValidacio(errors, avisos, "Codi postal", validarCodiPostal(codiPostal));
      if (errors.length > 0) {
        alert(`Errors trobats:\n${errors.join("\n")}`);
        return;
      }
      if (avisos.length > 0 && !window.confirm(`Avisos:\n${avisos.join("\n")}\n\nVols continuar?`)) {
        return;
      }
    }

    const payload = {
      idSucursals: sucursal?.idSucursals ?? null,
      Proveidors_idProveidors: proveidor?.idProveidors,
      Alies: alies.trim(),
      TelefonFixe: telefonFixe.trim(),
      TelefonMobil: telefonMobil.trim(),
      TelefonMobil2: telefonMobil2.trim(),
      Whatsapp: whatsapp.trim(),
      Email1: email1.trim(),
      Email2: email2.trim(),
      Horari: horari.trim(),
      Adreces: adreca,
    };

    try {
      let url = "";
      if (mode === "nou") url = `${API_URL}/Nousucursal.php`;
      else if (mode === "modificar") url = `${API_URL}/Modificarsucursal.php`;
      else if (mode === "esborrar") url = `${API_URL}/Esborrarsucursal.php`;
      else {
        onCancel();
        return;
      }

      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (data.success) {
        if (mode === "esborrar") onDeleted?.();
        else onSave?.({ ...payload, ...adreca, idSucursals: data.idSucursals || payload.idSucursals });
      } else {
        alert(`Error: ${data.message}`);
      }
    } catch (error) {
      console.error("Error gestió sucursals:", error);
      alert("S'ha produït un error en el procés.");
    }
  };

  const camp = (label, required, content, mida = "mig") => {
    const extra =
      mida === "ample" ? "dv2-field--ample" : mida === "llarg" ? "dv2-field--llarg" : mida === "curt" ? "dv2-field--curt" : "dv2-field--mig";
    return (
      <label className={`dv2-field ${extra}`}>
        <span className={`dv2-label ${required && !isReadOnly ? "dv2-required" : ""}`}>{label}</span>
        {content}
      </label>
    );
  };

  const potNavegar = Boolean(onPrev && onNext && navTotal > 1);

  return (
    <>
      {mode === "esborrar" && (
        <div className="dv2-banner dv2-banner--danger">Aquesta acció no es pot desfer. S'esborra la sucursal d'aquest proveïdor.</div>
      )}
      <div className="dv2-entity-body">
        <section className="dv2-card">
          <div className="dv2-card-head">
            <span className="dv2-card-num">1</span>
            <h2 className="dv2-card-title">Identificació</h2>
          </div>
          <div className="dv2-fields dv2-fields--sizes">
            {camp(
              "Àlies",
              true,
              isReadOnly ? (
                <span className="dv2-value">{alies || "—"}</span>
              ) : (
                <input className="dv2-input" type="text" value={alies} maxLength={50} autoFocus onChange={(e) => setAlies(e.target.value)} />
              ),
              "llarg"
            )}
            {camp(
              "Horari",
              false,
              isReadOnly ? (
                <span className="dv2-value">{horari || "—"}</span>
              ) : (
                <input className="dv2-input" type="text" value={horari} maxLength={100} onChange={(e) => setHorari(e.target.value)} />
              ),
              "llarg"
            )}
          </div>
        </section>

        <section className="dv2-card">
          <div className="dv2-card-head">
            <span className="dv2-card-num">2</span>
            <h2 className="dv2-card-title">Adreça</h2>
          </div>
          {isReadOnly ? (
            <div className="dv2-fields dv2-fields--sizes">
              {camp("Adreça", false, <span className="dv2-value">{formatAddress(adreca) || "—"}</span>, "ample")}
              {camp("Referències", false, <span className="dv2-value">{referencies || "Sense referències."}</span>, "ample")}
            </div>
          ) : (
            <div className="dv2-fields dv2-fields--sizes">
              {camp(
                "Tipus de via",
                true,
                <select className="dv2-select" value={tipusVia} onChange={(e) => setTipusVia(e.target.value)}>
                  <option value="">Selecciona</option>
                  {Object.keys(mapejos.tipusVia).map((option) => (
                    <option key={option} value={option}>
                      {mapToPresentable(option, mapejos.tipusVia)}
                    </option>
                  ))}
                </select>
              )}
              {camp(
                "Nom de la via",
                true,
                <input className="dv2-input" type="text" value={nomVia} maxLength={100} onChange={(e) => setNomVia(e.target.value)} />,
                "llarg"
              )}
              {camp(
                "Número",
                true,
                <input className="dv2-input" type="text" value={numVia} maxLength={15} onChange={(e) => setNumVia(e.target.value)} />,
                "curt"
              )}
              {camp(
                "Escala",
                false,
                <input className="dv2-input" type="text" value={escalaVia} maxLength={10} onChange={(e) => setEscalaVia(e.target.value)} />,
                "curt"
              )}
              {camp(
                "Pis",
                false,
                <input className="dv2-input" type="text" value={pis} maxLength={25} onChange={(e) => setPis(e.target.value)} />,
                "curt"
              )}
              {camp(
                "Porta",
                false,
                <input className="dv2-input" type="text" value={porta} maxLength={5} onChange={(e) => setPorta(e.target.value)} />,
                "curt"
              )}
              {camp(
                "Població",
                true,
                <input className="dv2-input" type="text" value={poblacio} maxLength={100} onChange={(e) => setPoblacio(e.target.value)} />
              )}
              {camp(
                "Província",
                true,
                <input className="dv2-input" type="text" value={provincia} maxLength={50} onChange={(e) => setProvincia(e.target.value)} />
              )}
              {camp(
                "País",
                true,
                <input className="dv2-input" type="text" value={pais} maxLength={100} onChange={(e) => setPais(e.target.value)} />
              )}
              {camp(
                "Codi postal",
                true,
                <input className="dv2-input" type="text" value={codiPostal} maxLength={10} onChange={(e) => setCodiPostal(e.target.value)} />,
                "curt"
              )}
              {camp(
                "Referències",
                false,
                <textarea className="dv2-textarea" value={referencies} maxLength={256} onChange={(e) => setReferencies(e.target.value)} />,
                "ample"
              )}
            </div>
          )}
        </section>

        <section className="dv2-card">
          <div className="dv2-card-head">
            <span className="dv2-card-num">3</span>
            <h2 className="dv2-card-title">Contacte</h2>
          </div>
          <div className="dv2-fields dv2-fields--sizes">
            {camp(
              "Telèfon fixe",
              false,
              isReadOnly ? (
                <span className="dv2-value">{telefonFixe || "—"}</span>
              ) : (
                <input className="dv2-input" type="text" value={telefonFixe} maxLength={20} onChange={(e) => setTelefonFixe(e.target.value)} />
              )
            )}
            {camp(
              "Mòbil 1",
              false,
              isReadOnly ? (
                <span className="dv2-value">{telefonMobil || "—"}</span>
              ) : (
                <input className="dv2-input" type="text" value={telefonMobil} maxLength={20} onChange={(e) => setTelefonMobil(e.target.value)} />
              )
            )}
            {camp(
              "Whatsapp",
              false,
              isReadOnly ? (
                <span className="dv2-value">{whatsapp || "—"}</span>
              ) : (
                <input className="dv2-input" type="text" value={whatsapp} maxLength={20} onChange={(e) => setWhatsapp(e.target.value)} />
              )
            )}
            {camp(
              "Mòbil 2",
              false,
              isReadOnly ? (
                <span className="dv2-value">{telefonMobil2 || "—"}</span>
              ) : (
                <input className="dv2-input" type="text" value={telefonMobil2} maxLength={20} onChange={(e) => setTelefonMobil2(e.target.value)} />
              )
            )}
            {camp(
              "E-mail 1",
              false,
              isReadOnly ? (
                <span className="dv2-value">{email1 || "—"}</span>
              ) : (
                <input className="dv2-input" type="email" value={email1} maxLength={45} onChange={(e) => setEmail1(e.target.value)} />
              ),
              "llarg"
            )}
            {camp(
              "E-mail 2",
              false,
              isReadOnly ? (
                <span className="dv2-value">{email2 || "—"}</span>
              ) : (
                <input className="dv2-input" type="email" value={email2} maxLength={45} onChange={(e) => setEmail2(e.target.value)} />
              ),
              "llarg"
            )}
          </div>
        </section>
      </div>
      {(potNavegar || mode === "nou" || mode === "modificar" || mode === "esborrar") && (
        <div className="dv2-entity-dock">
          {potNavegar && (
            <div className="dv2-recordNav">
              <button type="button" className="dv2-btn dv2-recordNav-btn" onClick={onPrev} title="Anterior">
                ‹
              </button>
              <span className="dv2-recordNav-info">
                {navIndex} / {navTotal}
              </span>
              <button type="button" className="dv2-btn dv2-recordNav-btn" onClick={onNext} title="Següent">
                ›
              </button>
            </div>
          )}
          {(mode === "nou" || mode === "modificar" || mode === "esborrar") && (
            <div className="dv2-footerActions">
              <button type="button" className="dv2-btn" onClick={onCancel}>
                Cancel·lar
              </button>
              {mode === "esborrar" ? (
                <button type="button" className="dv2-btn dv2-btn--danger" onClick={handleSubmit}>
                  Esborrar sucursal
                </button>
              ) : (
                <button type="button" className="dv2-btn dv2-btn--primary" onClick={handleSubmit}>
                  {mode === "nou" ? "Desar sucursal" : "Desar"}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
}

export default Sucursalsproveidor;
